import { useCallback, useState } from "react";
import { ReviewContent } from "./review-content";
import * as Styled from "./style";
import { useHandleClickByDevice } from "~/hooks/use-handle-click-by-device";
import { useMatchMedia } from "~/hooks/use-match-media";
import type { G_Review } from "~/lib/graphql";
import { usePostEvaluateReviewMutation, G_VoteStatus } from "~/lib/graphql";
import { useAuth } from "~/provider/auth";
import { formatDateToYMDDay } from "~/util/format-date";

export type ReviewProps = {
  /** 作品レビューのコメント情報 */
  reviewData: Omit<G_Review, "isConfirmed" | "postEvaluationCount">;
};

/**
 * ユーザーレビューを表示するcomponent
 * @param props {@link ReviewProps}
 */
export const ReviewInfo = (props: ReviewProps) => {
  const {
    reviewData: {
      id,
      reviewerName,
      reviewerId,
      title,
      point,
      hasSpoiler,
      comment,
      date,
      isReviewerPurchased,
      helpfulVoteCount,
    },
  } = props;

  const [postEvaluateReviewExec, { loading: isLoadingPostEvaluateReview }] = usePostEvaluateReviewMutation();

  const { isLoggedIn } = useAuth();
  const { isSpSize } = useMatchMedia();
  const { handleClickByDevice } = useHandleClickByDevice();

  const [voteStatus, setVoteStatus] = useState<G_VoteStatus | undefined>(undefined);

  const postEvaluateVideoReview = useCallback(async () => {
    if (isLoadingPostEvaluateReview) return;

    const { data } = await postEvaluateReviewExec({ variables: { input: { id, reviewerId } } });

    if (!data) return;

    const {
      evaluateReview: { mutationId, voteStatus },
    } = data;

    if (mutationId) {
      if (voteStatus === G_VoteStatus.Success) {
        setVoteStatus(G_VoteStatus.Success);
        return;
      }

      if (voteStatus === G_VoteStatus.Voted) {
        setVoteStatus(G_VoteStatus.Voted);
        return;
      }

      if (voteStatus === G_VoteStatus.SameReviewer) {
        setVoteStatus(G_VoteStatus.SameReviewer);
        return;
      }
    }
  }, [isLoadingPostEvaluateReview]);

  return (
    <Styled.ArtRootWrapper>
      {!isSpSize ? (
        <Styled.DivHeader>
          <Styled.ReviewInfoRatingStar size="M" starScore={point} isReadOnly />
          {hasSpoiler && <Styled.SpoilerTag bgColor="ERROR" tagText="ネタバレ" />}
          <Styled.Heading size="M">{title}</Styled.Heading>
        </Styled.DivHeader>
      ) : (
        <div>
          <Styled.DivHeader>
            <Styled.ReviewInfoRatingStar size="M" starScore={point} isReadOnly />
            {hasSpoiler && <Styled.SpoilerTag bgColor="ERROR" tagText="ネタバレ" />}
          </Styled.DivHeader>

          <Styled.Heading size="S">{title}</Styled.Heading>
        </div>
      )}

      <ReviewContent hasSpoiler={hasSpoiler} comment={comment ?? ""} />

      <Styled.DivReviewerBlock>
        {/** 仕様 : 投稿者の他の作品へのレビュー一覧（既存サービスページ）へ遷移する and 別タブ表示 */}
        <Styled.AnchorLinkWrapper>
          <Styled.AnchorLink
            href={`https://www.dmm.com/review/-/list/reviewer/=/id=${reviewerId}/`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {reviewerName}さんのレビュー
          </Styled.AnchorLink>
        </Styled.AnchorLinkWrapper>

        <Styled.TimePost>{formatDateToYMDDay(date)}</Styled.TimePost>
        {isReviewerPurchased && <Styled.TagPurchased color="GRAY500" borderColor="GRAY500" tagText="購入済み" />}
      </Styled.DivReviewerBlock>

      <Styled.DivEvaluationBlock>
        {isLoggedIn && (
          <Styled.DivPostEvaluation>
            {(() => {
              if (voteStatus === G_VoteStatus.Success) {
                return (
                  <Styled.ParVoteText>ご投票ありがとうございます。結果は24時間以内に反映されます。</Styled.ParVoteText>
                );
              }

              if (voteStatus === G_VoteStatus.Voted) {
                return <Styled.ParVoteText>このレビューはすでに投票済みです。</Styled.ParVoteText>;
              }

              if (voteStatus === G_VoteStatus.SameReviewer) {
                return <Styled.ParVoteText>ご自身で投稿したレビューに投票はできません。</Styled.ParVoteText>;
              }

              return (
                <>
                  <Styled.ParText>このレビューは参考になりましたか？</Styled.ParText>

                  <Styled.ButtonYes
                    name="yes-button"
                    mainText="はい"
                    color="GRAY900"
                    {...handleClickByDevice(postEvaluateVideoReview)}
                  />
                </>
              );
            })()}
          </Styled.DivPostEvaluation>
        )}

        <Styled.ParThankText>{helpfulVoteCount}人が参考になったと投票しています。</Styled.ParThankText>
      </Styled.DivEvaluationBlock>
    </Styled.ArtRootWrapper>
  );
};
